import React, { useState } from 'react';
import { v4 as uuidv4 } from "uuid";

interface SeasonCalendarItem {
    pageId: string;
    title: string;
    imageUrl: string,
    url: string;
    seasonMonths?: number[]; 
}

interface SeasonCalendarProps {
    seasonCalendar: SeasonCalendarItem[];
    months: string[];
    downloadUrl: string;
}



const SeasonCalendar: React.FC<SeasonCalendarProps> = ({ seasonCalendar, months, downloadUrl }) => {
    
    const [currentMonthId, setCurrentMonthId] = useState(new Date().getMonth());
    const currentMonthName = months[currentMonthId];
    const prev = () => setCurrentMonthId((prev) => currentMonthId < 1 ? 11 : (prev - 1));
    const prevMonthName = months[currentMonthId < 1 ? 11 : currentMonthId-1];
    const next = () => setCurrentMonthId((prev) => currentMonthId > 10 ? 0 : (prev + 1));
    const nextMonthName = months[currentMonthId > 10 ? 0 : currentMonthId+1];
    
    const filteredList = seasonCalendar.filter(item =>
        item.seasonMonths && item.seasonMonths.includes(currentMonthId+1)
    );
    
    return (
        <div className="w-full">
            <div className="flex relative items-center justify-center gap-5 mb-4 tablet:mb-5">

                {/*Navigation*/}
                <div
                    className="flex gap-2 justify-between tablet:justify-center w-full tablet:w-[400px] laptop:w-[600px]">

                    {/*Previous button*/}
                    <button
                        onClick={prev}
                        className="flex mr-5 items-center justify-center w-12 tablet:w-1/3"
                        aria-label="Forrige side"
                    >
                        {/*Tablet and larger*/}
                        <svg className="hidden tablet:block" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                             viewBox="0 0 24 24" fill="none">
                            <path d="M12 19L5 12M5 12L12 5M5 12H19" stroke="#222222" strokeWidth="2"
                                  strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>

                        {/*Mobile*/}
                        <svg className="tablet:hidden" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                             viewBox="0 0 24 24" fill="none">
                            <path d="M15 18L9 12L15 6" stroke="#222222" strokeWidth="2" strokeLinecap="round"
                                  strokeLinejoin="round"/>
                        </svg>

                        <span className="hidden tablet:block text-xl ml-[10px] tablet:w-[200px]">{prevMonthName}</span>
                    </button>

                    {/*Current month*/}
                    <div className="w-[260px] tablet:w-1/3">
                        <span
                            className="text-xl text-white flex justify-center items-center bg-black-text rounded-full border-black-text px-5 w-auto min-w-16 h-12">{currentMonthName}</span>
                    </div>

                    {/*Next button*/}
                    <button
                        onClick={next}
                        className="flex ml-5 items-center justify-center w-12 tablet:w-1/3"
                        aria-label="Neste side"
                    >
                        <span className="hidden tablet:block text-xl mr-[10px]">{nextMonthName}</span>

                        {/*Tablet and larger*/}
                        <svg className="hidden tablet:block" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                             viewBox="0 0 24 24" fill="none">
                            <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="#222222" strokeWidth="2"
                                  strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>

                        {/*Mobile*/}
                        <svg className="tablet:hidden" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                             viewBox="0 0 24 24" fill="none">
                            <path d="M9 18L15 12L9 6" stroke="#222222" strokeWidth="2" strokeLinecap="round"
                                  strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                    {/*Download button tablet and larger*/}
                    <div className="hidden tablet:block flex justify-end w-auto">
                        <a className="flex absolute right-0 bottom-2" href={downloadUrl} download>
                            <span className="text-xl mr-[10px]">Last ned</span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                 fill="none">
                                <path
                                    d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15M7 10L12 15M12 15L17 10M12 15V3"
                                    stroke="#222222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </a>
                    </div>
            </div>

            {/*Download button mobile*/}
            <div className="tablet:hidden flex justify-center w-auto h-12 p-3">
                <a className="flex" href={downloadUrl} download>
                    <span className="text-xl mr-[10px]">Last ned</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15M7 10L12 15M12 15L17 10M12 15V3" stroke="#222222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </a>
            </div>
            
            <ul className="grid grid-cols-1 tablet:grid-cols-2 laptop:grid-cols-4 gap-4 tablet:gap-5 mb-10">
                
                {filteredList.map((listItem) => {
                    return (
                        <li key={uuidv4()}>
                            <div
                                className="bg-green-50 h-full flex flex-col justify-start p-3 rounded-lg relative group focus-within:outline-[3px] focus-within:outline-red-secondary">
                                
                                    <figure className="overflow-hidden rounded-lg mb-2">
                                        <picture>
                                            <source media="(min-width: 1280px)"
                                                    srcSet={`${listItem.imageUrl}__w=366_h=186_autocrop=true`}/>
                                            <source media="(min-width: 1024px)"
                                                    srcSet={`${listItem.imageUrl}__w=280_h=142_autocrop=true`}/>
                                            <source media="(min-width: 768px)"
                                                    srcSet={`${listItem.imageUrl}__w=322_h=164_autocrop=true`}/>
                                            <img className="transition-all group-hover:scale-110 group-active:scale-105"
                                                 src={`${listItem.imageUrl}__w=711_h=361_autocrop=true`}
                                                 alt={`${listItem.title}`}/>
                                        </picture>
                                    </figure>

                                <h2 className="font-semibold text-xl laptop:text-2xl">{listItem.title}</h2>

                                <a href={listItem.url} title={listItem.title}
                                   className="absolute inset-0 focus-visible:outline-[3px] focus-visible:outline-red-secondary rounded-lg">
                                    <span className="sr-only">{listItem.title}</span>
                                </a>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default SeasonCalendar;
